import type { KitManifest } from '../../shared/discoverKits';

const manifest: KitManifest = {
    slug: 'network-editor',
    context: 'size2mix',
    name: 'Éditeur de réseau',
    description:
        "Éditeur cartographique d'un réseau Nemo (Cesium + Mapbox) : carte 3D, formulaires d'entité, recherche d'entité, toolbar, batch save. Le code réel est packagé en bibliothèque interne sous src/ui/library/nemo-network-editor.",
    status: 'partial',
    owner: 'platform-team',
    tags: [
        'network-editor',
        'network',
        'map',
        'cesium',
        'mapbox',
        '3d',
        'entity-form',
        'editor',
    ],
    nemoSources: [
        {
            label: 'Page racine',
            path: 'apps/frontend-next/src/ui/views/pages/network/NetworkEditor.tsx',
            note: 'AppNetworkEditor : assemble Editor (Cesium) + NetworkToolbar + EntityForm + EntitySearchFormWrapper. Gère plein écran, batch save, fetch des données réseau.',
        },
        {
            label: 'Route',
            path: 'apps/frontend-next/src/ui/views/pages/network/NetworkEditorRoute.tsx',
            note: "Wrapper de routage : récupère networkEid depuis l'URL et les préférences user.",
        },
        {
            label: 'Bibliothèque interne',
            path: 'apps/frontend-next/src/ui/library/nemo-network-editor/',
            note: "Lib in-repo. Contient src/network_editor/components/* (NetworkEditor, NetworkToolbar, EntityForm, EntitySearchFormWrapper) et src/network_editor/cesium/* (Editor 3D, opérations).",
        },
        {
            label: 'Editor Cesium',
            path: 'apps/frontend-next/src/ui/library/nemo-network-editor/src/network_editor/cesium/editor/Editor.ts',
            note: "Cœur de l'éditeur 3D Cesium : rendu, sélection, hover, opérations.",
        },
        {
            label: 'Operations Saver',
            path: 'apps/frontend-next/src/ui/library/nemo-network-editor/src/network_editor/cesium/operations/OperationsSaver.ts',
            note: 'Système de batching des opérations utilisateur avant save.',
        },
        {
            label: 'Hooks de fetch',
            path: 'apps/frontend-next/src/ui/hooks/network/network-editor/',
            note: 'useFetchUnitsResult, useFetchNetworkSchemaResultByEid — données réseau et schéma.',
        },
        {
            label: 'Documentation',
            path: 'docs/network-editor/README.md',
            note: 'Architecture, fonctionnel, technique, API Playwright.',
        },
    ],
    sharedComponents: [
        '@shared/PrototypeStudioLayout',
        '@shared/modal/RightSideModal (panneau formulaire d\'entité)',
        '@shared/EditableField (champs inline dans EntityForm)',
        '@shared/AccordionItem (sections du formulaire d\'entité)',
        // Note : pas (encore) de @shared/network-editor/. La carte Cesium
        // est trop lourde à mocker. Pour un proto, mock visuel statique
        // (image, SVG) ou stub Mapbox simple.
    ],
    views: [
        {
            id: 'map-3d',
            label: 'Carte 3D — Vue principale',
            description:
                'Vue Cesium 3D du réseau (tuyaux, nœuds, assets) avec interactions souris : zoom, rotation, sélection.',
            prototypeSlug: 'ref-topology-editor',
            screenshot: '01. network editor map view.png',
        },
        {
            id: 'network-toolbar',
            label: 'Toolbar',
            description:
                'Barre d\'outils flottante : modes (sélection, ajout, mesure), undo/redo, plein écran, save.',
            prototypeSlug: 'ref-topology-editor',
            screenshot: '01. network editor map view.png',
        },
        {
            id: 'entity-form',
            label: 'Panneau — Formulaire d\'entité',
            description:
                "Panneau latéral droit : édition de l'entité sélectionnée (tuyau, nœud, asset). Sections en accordéon.",
            prototypeSlug: 'ref-topology-editor',
            screenshot: '04-entity-detail-sidebar.png.png',
        },
        {
            id: 'entity-search',
            label: 'Recherche d\'entité',
            description:
                "Wrapper de recherche : filtre les entités du réseau par nom/type, focus la carte sur la sélection.",
            prototypeSlug: 'ref-topology-editor',
            screenshot: '02.network-editor-map-view-research-form.png',
        },
        {
            id: 'fullscreen-mode',
            label: 'Mode plein écran',
            description:
                "Bascule plein écran de la carte (geste useFullScreen). Hide chrome, max canvas.",
        },
        {
            id: 'batch-save',
            label: 'Batch save / undo',
            description:
                "Comportement BatchSaver : accumulation des opérations utilisateur, save groupé, indicateur de modifications non sauvegardées.",
            prototypeSlug: 'ref-topology-editor',
            screenshot: '03.network-editor-edit-selected-entities.png',
        },
    ],
    notes: `
- Manifest initial : pas de screenshots ni de kit partagé. Le réel s'appuie
  sur Cesium (lourd, payant en token Mapbox) → un kit partagé n'a de sens
  qu'autour des panneaux annexes (EntityForm, Toolbar, Search), pas la carte
  elle-même. Pour la carte, mock visuel suffit en proto.
- Si plusieurs protos ciblent l'EntityForm, extraire @shared/network-editor/
  EntityForm.tsx (Provider + sections accordéon + boutons save/cancel).
- Voir docs/network-editor/ pour l'architecture détaillée.
`.trim(),
};

export default manifest;
